import { random } from 'lodash';
import queryString from 'query-string';
import { useEffect } from 'react';
import { message } from 'antd';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { IExtraInfo, ILayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui';
import { packageTemplateConf } from '@/hooks/fetch-template';
import { isPPTDemoAndShowFullTemplate } from '@/hooks/fetch-template/editor-mode';
import { transferLayersToClientDoText, transferTemplateToClient } from '@/hooks/fetch-template/server-to-client';
import { type IEditor } from '@/instance';
import {
    fetchAIPPTComponentList,
    fetchAIPPTMaterialDetail,
    fetchAIPPTMaterialDetailMultiV2,
    fetchAIPPTTemplateList,
} from '@/services/ai';
import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import { adjustImageLayerImage, findTemplateDirectoryLayout, Layer, PPTTemplate } from './apply-ppt-template';
import { pptContentAllJSON } from './ppt-content-all';
import { pptContentJSON } from './ppt-content-json';
import { smartComponentContentJson } from './smart-component-content-json';

const useFetchAndApplyPPTDemo = (editor: IEditor) => {
    const setCollapseType = useCollapseModel((state) => state.setCurrentType);
    useEffect(() => {
        if (!isPPTDemoAndShowFullTemplate()) {
            return;
        }

        let fonts = editor.services.font.getFonts();
        const getInitData = async () => {
            fonts = editor.services.font.getFonts();
            if (!fonts || fonts.length === 0) {
                setTimeout(() => {
                    getInitData();
                }, 1000);
                return;
            }
            try {
                editor.services.mask.show({
                    loading: true,
                });
                const layerAndStageIndexMap: { [layerId: string]: number } = {};
                const queryParams: any = queryString.parse(window.location.search);
                const { componentId, templateId, smartComponentId } = queryParams;
                const componentDatas = [] as any[];
                let smartComponentData = null as any;
                let templateData;

                if (templateId) {
                    const rsp = (await fetchAIPPTMaterialDetail(templateId)).data.data;
                    templateData = JSON.parse(rsp.formula);
                } else {
                    const rsp = await fetchAIPPTTemplateList();

                    const templateList = rsp.data.data.list[0].templates.map((item: any) => {
                        return {
                            id: item.template_id,
                        };
                    });
                    const templateDataId = templateList[random(0, templateList.length - 1)]?.id;

                    if (templateDataId) {
                        const rsp = (await fetchAIPPTMaterialDetail(templateDataId)).data.data;
                        templateData = JSON.parse(rsp.formula);
                    }
                }
                if (!templateData) {
                    return;
                }

                const rsp = await fetchAIPPTComponentList();
                const componentList = rsp.data.data.data;
                if (componentId) {
                    const details = await fetchAIPPTMaterialDetailMultiV2(componentId);

                    details.data.data.forEach((detail: any) => {
                        componentDatas.push(detail);
                    });
                } else if (smartComponentId) {
                    // 去pptContent数据
                    // 获取普通组件和智能组件详情
                    // 将智能组件插入到普通组件中

                    const contentData = smartComponentContentJson;
                    const componentId = contentData.component_id;
                    const details = await fetchAIPPTMaterialDetailMultiV2(
                        `${componentId.toString()},${smartComponentId}`,
                    );
                    componentDatas.push(details.data.data[0]);
                    smartComponentData = details.data.data[1];
                }

                const transferToClient = async (templateData: any) => {
                    const data = await transferTemplateToClient(templateData, fonts, '', false);
                    return data.template.templateConf;
                };

                let pptContent = pptContentJSON as any;

                console.log('pptContentJSON===', pptContentJSON);

                const clientData = await transferTemplateToClient(templateData, fonts, '', false, true);

                templateData = clientData.template;
                const directoryLayout = findTemplateDirectoryLayout(templateData.templateConf);
                if (smartComponentData) {
                    const contentData = smartComponentContentJson;
                    pptContent = [contentData];
                    const smartComponentDataTemplateConf = await transferToClient(
                        JSON.parse(smartComponentData.formula),
                    );
                    const componentDataTemplateConf = await transferToClient(JSON.parse(componentDatas[0].formula));

                    await PPTTemplate.insertSmartComponentToComponent(
                        componentDataTemplateConf,
                        smartComponentDataTemplateConf,
                        contentData,
                    );

                    pptContent[0].componentData = {
                        templateConf: componentDataTemplateConf,
                    };
                } else if (componentDatas && componentDatas.length > 0) {
                    pptContent = pptContentAllJSON;
                    pptContent = await PPTTemplate.getPPTContentDataByComponents(
                        pptContent,
                        transferToClient,
                        componentDatas,
                    );
                } else {
                    pptContent = await PPTTemplate.getComponentsByPPTContentData(
                        pptContent as any,
                        componentList,
                        transferToClient,
                        directoryLayout,
                        componentDatas,
                    );
                }

                const templateConf = PPTTemplate.generate({
                    pptContents: pptContent,
                    templateData,
                });

                const allLayers = [] as ILayerProps[];
                const allImageLayers = [] as ILayerProps[];
                templateConf.forEach((item) => {
                    traverseTrees(item.layers, (layer: ILayerProps) => {
                        if (layer.layerType === LayerType.TEXT_LAYER) {
                            allLayers.push(layer);
                        }
                        if (layer.layerType === LayerType.IMAGE_LAYER) {
                            allImageLayers.push(layer);
                        }
                    });
                });

                for (let i = 0; i < allImageLayers.length; i++) {
                    const layer = allImageLayers[i];
                    await adjustImageLayerImage(layer);
                }

                templateConf.forEach((item, index) => {
                    traverseTrees(item.layers, (layer: ILayerProps) => {
                        layerAndStageIndexMap[layer.id] = index;

                        if (layer.layerType === LayerType.TEXT_LAYER) {
                            const { extraInfo } = layer;

                            if (extraInfo?.label) {
                                const { textExpend } = extraInfo.label;
                                if (textExpend) {
                                    Layer.adjustTextSize(layer, textExpend);
                                }
                            }
                        }
                    });
                });

                await transferLayersToClientDoText(allLayers, fonts, layerAndStageIndexMap, false);
                console.log('templateConf===', templateConf);

                const { stages, extraInfos } = packageTemplateConf(templateConf, {}, true);
                await editor.services.stage.insertWithoutHistory(stages, extraInfos, undefined, true);
                setCollapseType(CollapseType.NAVIGATOR_MENU);
                editor.services.mask.hide();
            } catch (error) {
                editor.services.mask.hide();
                console.error('error===', error);
                message.error('套版出错 保留现场，请联系开发人员');
            }
        };

        getInitData();
    }, []);
};

export default useFetchAndApplyPPTDemo;
